import React, { useState } from 'react';
import { Typography } from 'antd';

import UserAvatar from './UserAvatar';
import UserInfoModal from './UserInfoModal';

const { Text } = Typography;

/**
 * THE way a user renders anywhere in keepr: sharing rows, audience reports,
 * group tags, admin tables, picker options. Avatar (UserAvatar — photo/icon/
 * initial) plus the person's name, as ONE text node so every surface can be
 * asserted and searched by the name a human would read.
 *
 *   <UserChip user={user} />                                  // the default
 *   <UserChip user={u} fallbackId={rawId} />                  // id when unresolvable
 *   <UserChip user={u} interactive={false} />                 // picker options
 *   <UserChip user={u} avatarOnly size={24} />                // Avatar.Group stacks
 *   <UserChip user={u} action={{ label: 'Open item', to }} /> // with context
 *
 * When `interactive` (the default) the avatar is a real button that opens
 * UserInfoModal — "who is this?" is one click away from every mention of a
 * person. Turn it OFF inside anything that is already a control: a dropdown
 * option must select, not open a modal.
 *
 * `fallbackId` preserves the sharing surfaces' behavior for a grant whose
 * principal can no longer be read: show the id rather than the 'Unknown user'
 * sentinel, as secondary text so it reads as a fallback, not as a name.
 *
 * `action` is passed straight through to the modal as its context button.
 */

/** Canonical display name for a public-projection user. */
export function userLabel(user) {
	return user?.fullName
		|| [user?.firstName, user?.lastName].filter(Boolean).join(' ')
		|| user?.email
		|| 'Unknown user';
}

export default function UserChip({
	user,
	fallbackId,
	size = 24,
	interactive = true,
	avatarOnly = false,
	action,
	className = '',
}) {
	const [infoOpen, setInfoOpen] = useState(false);

	const name = userLabel(user);
	const unresolved = name === 'Unknown user' && !!fallbackId;
	const displayName = unresolved ? fallbackId : name;

	const avatar = <UserAvatar user={user} size={size} />;

	// A real button, with no button chrome: the affordance is the pointer and
	// the accessible name, so a chip never looks like a control in a table row.
	const avatarNode = interactive ? (
		<button
			type="button"
			className="user-chip__avatar-btn"
			style={{ '--chip-avatar-size': `${size}px` }}
			aria-label={`About ${displayName}`}
			onClick={() => setInfoOpen(true)}
		>
			{avatar}
		</button>
	) : avatar;

	// Mounted only once opened — a table of 100 chips is 100 avatars, not 100
	// modals.
	const modal = interactive && infoOpen ? (
		<UserInfoModal
			open
			user={user}
			fallbackId={fallbackId}
			action={action}
			onClose={() => setInfoOpen(false)}
		/>
	) : null;

	const classes = ['user-chip', avatarOnly ? 'user-chip--avatar-only' : '', className]
		.filter(Boolean).join(' ');

	// One element, always: Avatar.Group clones its children (and passes them a
	// style prop), so a Fragment here would break the stack.
	return (
		<span className={classes} title={avatarOnly ? displayName : undefined}>
			{avatarNode}
			{avatarOnly ? null : (
				unresolved
					? <Text type="secondary" className="user-chip__name">{displayName}</Text>
					: <span className="user-chip__name">{displayName}</span>
			)}
			{modal}
		</span>
	);
}
